import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { toast } from 'react-toastify';
import api from '../../utils/api';

export default function AdminDataBackups() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreId, setRestoreId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/data-backups', { params: { page: 1, limit: 100 } });
      const d = r.data?.data;
      setItems(d?.items || []);
      setTotal(d?.total ?? 0);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load backups');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (id) => {
    try {
      const r = await api.get(`/admin/data-backups/${encodeURIComponent(id)}`);
      setDetail(r.data?.data || null);
      setDetailOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load detail');
    }
  };

  const download = async (id, filename) => {
    try {
      const r = await api.get(`/admin/data-backups/${encodeURIComponent(id)}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([r.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'backup.zip';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Download failed');
    }
  };

  const runRestore = async () => {
    if (!restoreId) return;
    try {
      const r = await api.post(`/admin/data-backups/${encodeURIComponent(restoreId)}/restore`);
      toast.success(r.data?.message || 'Restore completed');
      setRestoreOpen(false);
      setRestoreId(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Restore failed');
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Backed Up Data
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Zip files created from Data Administration → Archived Data (or the same API). Stored on the server under{' '}
        <strong>DataBackup</strong>. Restore merges JSON from <strong>manifest.json</strong> into existing rows by id (clears archive
        flags). Missing users are not recreated without password hashes.
      </Typography>

      <Alert severity="warning" sx={{ mb: 2 }}>
        Restore may fail if foreign-key parents were permanently deleted. Test on a copy of production before relying on this path.
      </Alert>

      {loading ? (
        <CircularProgress />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>File</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Created by</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary">No backups yet. Export from Archived Data.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((row) => (
                <TableRow key={row.id}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{row.filename}</TableCell>
                  <TableCell>{row.size_bytes != null ? `${(Number(row.size_bytes) / 1024).toFixed(1)} KB` : '—'}</TableCell>
                  <TableCell>{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</TableCell>
                  <TableCell>
                    {row.creator
                      ? `${row.creator.first_name || ''} ${row.creator.last_name || ''} (${row.creator.email})`.trim()
                      : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                      <Button size="small" onClick={() => openDetail(row.id)}>
                        Details
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => download(row.id, row.filename)}>
                        Download
                      </Button>
                      <Button
                        size="small"
                        color="warning"
                        variant="contained"
                        onClick={() => {
                          setRestoreId(row.id);
                          setRestoreOpen(true);
                        }}
                      >
                        Restore from zip
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Total backups: {total}
      </Typography>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Backup detail</DialogTitle>
        <DialogContent dividers>
          {detail ? (
            <Box component="pre" sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, overflow: 'auto', fontSize: 12 }}>
              {JSON.stringify(detail, null, 2)}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={restoreOpen} onClose={() => setRestoreOpen(false)}>
        <DialogTitle>Restore from backup?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Applies manifest entities to the database. Existing rows are updated and un-archived. This does not delete newer data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={runRestore}>
            Restore
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
